<script>
export default {
  name: 'ReturnBox'
}
</script>

<script setup>
import { ref, reactive, onMounted, computed, nextTick } from 'vue';

import Pagination from '@parts/Pagination.vue';
import ReturnNavigation from '@parts/ReturnNavigation.vue';
import ReturnGoodsList from '@parts/ReturnGoodsList.vue';
import axios from 'axios';
import { apiError } from '@functions/apiError.js';
import ApiErrorModal from '@parts/ApiErrorModal.vue';
import { modalUtil } from '@functions/modalUtil.js';
import DeleteFromReturnBoxCompleteModal from './modal/DeleteFromReturnBoxCompleteModal.vue';
import ReturnConfirmModal from './modal/ReturnConfirmModal.vue';
import ReturnCompleteModal from './modal/ReturnCompleteModal.vue';
import TimeLimitOverConfirmModal from '@parts/modal/TimeLimitOverConfirmModal.vue';
import { url } from '@/config/urls';
import router from '@/router/index.js';
import { useRoute } from 'vue-router';

// 外部定義メソッド
const { showModal, switchFromCurrentModal } = modalUtil();
const route = useRoute();

// 画面ID
const screenId = ref("ReturnBox");

// エラーメッセージの状態管理
const errorMessages = ref([]);

// 定数定義
const pageSizeArray = [50, 100, 200];

// Prop定義
const props = defineProps({
  userDataUrl: {
    type: String,
    default: url.api.userData
  },
  goodsTypesApiUrl: { 
    type: String,
    default: url.api.goodsTypes
  },
  returnBoxListApiUrl: {
    type: String,
    default: url.api.returnBox.list
  },
  returnBoxCountApiUrl: {
    type: String,
    default: url.api.returnBox.count
  },
  doReturnApiUrl: {
    type: String,
    default: url.api.doReturn
  },
  returnSearchPageUrl: {
    type: String,
    default: url.page.returnSearch
  },
  returnBoxPageUrl: {
    type: String,
    default: url.page.returnBox
  },
  errorUrl: {
    type: String,
    default: url.error
  },
  loadingIcon: {
    type: String,
    default: url.static.loadingIcon
  }
});
// ユーザー情報
const user = reactive({
  isPermittedReturnRequest: null  // null: 未取得, true: 権限あり, false: 権限なし
});

// ユーザー情報取得APIの呼び出し
const getUserInfo = async () => {
  try {
    const response = await axios.get(props.userDataUrl);
    user.isPermittedReturnRequest = response.data.response.isPermittedReturnRequest;
  } catch (error) {
    apiError(error, errorMessages);
  }
}

// 商品一覧取得APIリクエストパラメータ
const condition = ref({
  size: 50,
  page: 1
});

// ページネーション
const pagingParameter = ref({
  pageSize: 50,
  pageNum: 1
});
const totalPage = ref(1);

// 商品分類マスタデータ
const goodsTypes = ref([]);

// 検索結果
const goodsList = ref([]);

// 返品ボックス更新日時
const returnBoxUpdatedAt = ref(null);

// ローディングフラグ
const isLoading = ref(false);

// ローディングフラグ更新
const handleUpdateLoading = (loading) => {
  isLoading.value = loading;
}

// エラー処理
const handleApiError = (error) => {
	apiError(error, errorMessages);
};

// エラーフラグをオブジェクトで管理
const validationErrors = reactive({})

// バリデーションエラーの更新
const validationErrorHandle = (key, value) => {
  validationErrors[key] = value
}

// 全体エラー判定
const hasError = computed(() => {
  return Object.values(validationErrors).some(v => v)
})

// 返品ボックス行数
const totalNum = ref(0);
// 返品ボックス数量合計
const totalQuantity = ref(0);
// 返品ボックス行数集計
const countReturnBox = () => {
  return axios.get(props.returnBoxCountApiUrl)
    .then((response) => {
      totalNum.value = parseInt(response.data.response.totalNum);
      totalQuantity.value = parseInt(response.data.response.totalQuantity);
      return getGoodsList();
    })
    .catch((error) => {
      apiError(error, errorMessages);
    });
}

// 商品分類マスタデータ取得処理
const getGoodsTypes = () => {
  axios.get(props.goodsTypesApiUrl)
    .then((response) => {
      goodsTypes.value = response.data.response.goodsTypes
      .filter(item => item.code !== "02" && item.code !== "04");
    })
    .catch((error) => {
      apiError(error, errorMessages);
    });
}

// 商品一覧取得処理
const getGoodsList = async() => {
  handleUpdateLoading(true);
  try {
    const response = await axios.get(
      props.returnBoxListApiUrl
      , {
        params: condition.value
      }
    )
    goodsList.value = response.data.response.goodsList ?? [];
    totalNum.value = parseInt(response.data.response.totalNum);
    totalQuantity.value = parseInt(response.data.response.totalQuantity);
    pagingParameter.value.pageNum = parseInt(response.data.response.currentPage);
    totalPage.value = Math.ceil(totalNum.value / pagingParameter.value.pageSize);
    totalPage.value = totalPage.value ? totalPage.value : 1;
    // 最終更新日時を保持
    returnBoxUpdatedAt.value = response.data.response.returnBoxUpdatedAt;
  } catch (error){
      apiError(error, errorMessages);
  } finally {
      handleUpdateLoading(false);
  }
}

// ページネーション処理
const onChangePageNum = (page) => {
  if (!Number.isFinite(page)) {
    return;
  }
  condition.value.page = page;
  getGoodsList();

  pagingParameter.value.pageNum = page;

  window.scroll({
      top: 0,
      behavior: 'smooth'
  });
}

const onChangePageSize = (selected) => {
  condition.value.page = 1;
  condition.value.size = selected;
  getGoodsList();

  pagingParameter.value.pageNum = 1;
  pagingParameter.value.pageSize = selected;

}

// 検索条件のセット
const setCondition = () => {
  condition.value.size = pagingParameter.value.pageSize || 50;
  condition.value.page = 1;
  pagingParameter.value.pageNum = 1;
}

// 返品依頼TOPページへ移動
const goToReturnSearch = () => {
  router.push(props.returnSearchPageUrl);
}

// 「返品する」押下時処理
let isCheckingReturn = false;
const onClickDoReturn = () => {
  if (isCheckingReturn) return;
  isCheckingReturn = true;
  handleUpdateLoading(true);
  axios.get(props.returnBoxCountApiUrl)
    .then((response) => {
      if(response.data.response.timeLimitOverNum > 0) {
        showModal('timeLimitOverConfirmModal');
      } else {
        showModal('returnConfirmModal');
      }
    })
    .catch((error) => {
      apiError(error, errorMessages);
    })
    .finally(() => {
      handleUpdateLoading(false);
      isCheckingReturn = false;
    });
}

// 伝票日付3ヵ月経過警告モーダル「はい」押下時処理
const openReturnConfirmModal = () => {
  switchFromCurrentModal('returnConfirmModal');
}

// 返品確認モーダル「はい」押下時処理
let isReturning = false;
const doReturn = async() => {
  if (isReturning) return;
  isReturning = true;
  handleUpdateLoading(true);
  try {
    const response = await axios.post(props.doReturnApiUrl, {
      returnBoxUpdatedAt: returnBoxUpdatedAt.value
    });

    await countReturnBox();

    await nextTick();

    bootstrap.Modal.getInstance(
      document.getElementById('returnConfirmModal')
    )?.hide();

    setTimeout(() => {
      showModal('returnCompleteModal');
    }, 200);
  } catch(error) {
      apiError(error, errorMessages);
  }finally {
      handleUpdateLoading(false);
      isReturning = false;
  };
}

// URlパラメータを使用してモーダルを表示
const showModalWithUrlParam = () => {
  // URLパラメータに応じてモーダル表示
  const modalMap = {
    'deleted': 'deleteFromReturnBoxCompleteModal',
  };

  for (const [param, modalId] of Object.entries(modalMap)) {
    if (route.query[param] === 'true') {

      // 対象クエリだけ除外
      const { [param]: _, ...restQuery } = route.query;
      router.replace({ query: restQuery });

      // 画面読み込み完了後にモーダル表示
      nextTick(() => {
        setTimeout(() => showModal(modalId), 100);
      });
      break;
    }
  }
}

const handleReturnBoxDeleted = async() => {
  document.querySelectorAll('.modal.show').forEach(el => {
    bootstrap.Modal.getInstance(el)?.hide();
  });

  document.querySelectorAll('.modal-backdrop').forEach(el => el.remove());
  document.body.classList.remove('modal-open');

  try {
    await countReturnBox();

    await nextTick();

    setTimeout(() => {
      showModal('deleteFromReturnBoxCompleteModal');
    }, 200);
  } catch(error) {
    router.push(props.error);
  }
}

// 初期処理
onMounted(async () => {
  await getUserInfo();
  if (!user.isPermittedReturnRequest) {
    return;
  }

  await nextTick();
  showModalWithUrlParam();

  getGoodsTypes();
  getGoodsList();
});

</script>

<template>

  <div id="contents1" class="bg3" v-if="user.isPermittedReturnRequest">
    <ReturnNavigation
      :screenId = "screenId"
      :returnBoxTotalRecord = totalNum
    ></ReturnNavigation>

    <template v-if="goodsList.length === 0">
      <h2 class="com_title1 com_mgb5">返品ボックス</h2>
      <p class="com_center1 com_fs16">返品ボックス内は空です</p>
      <div class="com_center1 com_mgb20">
        <button type="button" class="btn com_btn2 wd1" @click="goToReturnSearch">返品依頼TOP</button>
      </div>
    </template>
    <template v-else>
      <h2 class="com_title1 com_mgb5">返品ボックス</h2>
      <p class="com_center1 com_fs16">以下の商品を返品依頼に出しますか</p>
      <p class="com_center1 com_fs16 com_strong1">
        行数：<span class="com_color1">{{ totalNum }}</span>&emsp;
        数量合計：<span class="com_color1">{{ totalQuantity }}</span></p>
      <div class="com_center1 com_mgb20">
        <button type="button" class="btn com_btn1 wd1" @click="onClickDoReturn"
        :disabled="hasError">返品依頼する</button>
      </div>
    </template>

    <div v-if="goodsList.length > 0" class="com_inner5 clearfix">

      <Pagination
        :isShowFirstAndLastBtn="true"
        :isShowPaginationInfo="true"
        :totalNum="totalNum"
        :currentPage="pagingParameter.pageNum"
        :totalPage="totalPage"
        :pageSize="pagingParameter.pageSize"
        :pageSizeSelectList="pageSizeArray"
        @changePageNum="onChangePageNum"
        @changePageSize="onChangePageSize"
      ></Pagination>

      <ReturnGoodsList
        :screenId = "screenId"
        :goodsList = goodsList
        :goodsTypes = goodsTypes
        @updateLoading="handleUpdateLoading"
        @countReturnBox="countReturnBox"
        @apiError="handleApiError"
        @validationError="validationErrorHandle"
        @returnBoxDeleted="handleReturnBoxDeleted"
      ></ReturnGoodsList>

      <Pagination
        :isShowFirstAndLastBtn="true"
        :isShowPaginationInfo="true"
        :totalNum="totalNum"
        :currentPage="pagingParameter.pageNum"
        :totalPage="totalPage"
        :pageSize="pagingParameter.pageSize"
        :pageSizeSelectList="pageSizeArray"
        @changePageNum="onChangePageNum"
        @changePageSize="onChangePageSize"
      ></Pagination>

    </div>
    
  <!-- 削除完了モーダル -->
  <DeleteFromReturnBoxCompleteModal></DeleteFromReturnBoxCompleteModal>

  <!-- 伝票日付3ヵ月経過警告モーダル -->
  <TimeLimitOverConfirmModal
    @timeLimitOverConfirmed="openReturnConfirmModal"
  ></TimeLimitOverConfirmModal>

  <!-- 返品確認モーダル -->
  <ReturnConfirmModal
    @doReturn="doReturn"
  ></ReturnConfirmModal>

  <!-- 返品完了モーダル -->
  <ReturnCompleteModal></ReturnCompleteModal>

  </div>

  <ApiErrorModal
    :messages="errorMessages"
  ></ApiErrorModal>

  <Transition>
    <div class="loading-animation" v-show="isLoading">
      <div class="show-icon-box">
        <img :src="props.loadingIcon" />
      </div>
    </div>
  </Transition>

</template>
